import { useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ReCAPTCHA from 'react-google-recaptcha';

import axios from 'axios';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';

import { BarsLoadingIndicator } from 'components/common/barsLoadingIndicator';
import { Input } from '../common/input';
import { Modal } from 'components/common/modal';

import { closeModal } from 'features/modalSlice';

import { apiConfig } from '../../config';
import { useGetBookingData } from 'hooks';
import { getDifferenceInDays } from 'lib';
import { noReCaptchaPublicKey } from '../../constants';

import './ShareHotelModal.css';

const ShareHotelModal = () => {
    const dispatch = useDispatch();
    const [fromEmail, setFromEmail] = useState('');
    const [toEmail, setToEmail] = useState('');
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState({});
    const [reCaptchaValue, setReCaptchaValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const reCaptchaRef = useRef(null);

    const colors = useSelector(
        (state) => state.miniBookingWidget?.data?.colors
    );
    const Lang = useSelector((state) => state.miniBookingWidget?.data?.Lang);
    const propertyID = useSelector(
        (state) => state.miniBookingWidget?.data?.propertyID
    );
    const reCapatchPublicKey =
        process.env.NODE_ENV === 'production'
            ? useSelector(
                  (state) => state.miniBookingWidget?.data?.reCaptchaPublicKey
              )
            : process.env.REACT_APP_RE_CAPTCHA_PUBLIC_KEY;
    const shareLink = useSelector(
        (state) => state.miniBookingWidget?.data?.shareLink
    );

    // On production ignore reCaptcha if the public key is not provided
    const ignoreReCaptch = !reCapatchPublicKey;

    const isOpen = useSelector((state) => state.modal.isShareHotelModalOpen);

    const handleModalClose = useCallback(() => {
        dispatch(closeModal({ modalId: 'ShareHotelModal' }));
        // Reset
        setErrors({});
        setFromEmail('');
        setToEmail('');
        setMessage('');
        setReCaptchaValue('');
    });

    const { userBookingData } = useGetBookingData();

    const handleSubmit = async (event) => {
        event.preventDefault();
        const errors = validateInputs();
        const token = reCapatchPublicKey
            ? reCaptchaRef.current.getValue()
            : noReCaptchaPublicKey;

        if (!isEmpty(errors)) {
            setErrors(errors);
            return;
        }

        const selectedDays = getDifferenceInDays(
            userBookingData?.checkout,
            userBookingData?.checkin
        );

        let bookLink = `${apiConfig.reserveOnlineUrl}?src=470^adults=${userBookingData?.adults}`;
        bookLink += `^children=${userBookingData?.children}^lang=${userBookingData?.localeLanguage}`;
        bookLink += `^currency=${userBookingData?.currency}^checkin=${userBookingData?.checkin}`;
        bookLink += `^checkout=${userBookingData?.checkout}^nights=${selectedDays}`;

        try {
            setIsLoading(true);
            const response = await axios.post(apiConfig?.shareHotel, {
                dateFrom: userBookingData?.checkin,
                dateTo: userBookingData?.checkout,
                lang: userBookingData?.localeLanguage,
                mailfrom: fromEmail,
                mailto: toEmail,
                message,
                token,
                propertyID,
                nights: selectedDays,
                bookLink,
            });

            if (response?.data.toLowerCase() === 'ok') {
                toast.success(Lang?.successMessage, {
                    autoClose: true,
                    theme: 'colored',
                });
            }
        } catch (error) {
            toast.error(Lang?.networkError, {
                autoClose: true,
                theme: 'colored',
            });
        }

        handleModalClose();
        setIsLoading(false);

        if (reCaptchaRef.current) {
            reCaptchaRef.current.reset();
        }
    };

    // TODO: IMPROVE THIS (KEEP IT DRY)
    const validateInputs = () => {
        const errors = {};

        const emailRegex =
            // eslint-disable-next-line no-useless-escape
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if (!fromEmail) {
            errors.fromEmail = Lang?.requiredField;
        } else if (!fromEmail.match(emailRegex)) {
            errors.fromEmail = Lang?.pleaseEnterValidEmail;
        }

        if (!toEmail) {
            errors.toEmail = Lang?.requiredField;
        } else if (!toEmail.match(emailRegex)) {
            errors.fromEmail = Lang?.pleaseEnterValidEmail;
        }

        if (!message) {
            errors.message = Lang?.requiredField;
        }

        if (!ignoreReCaptch && !reCaptchaValue) {
            errors.reCaptcha = Lang?.pleaseCompleteRecaptcha;
        }

        return errors;
    };

    const renderContent = () => {
        return (
            <div className="ab-share-form-cnt ab-container">
                <form
                    className="ab-share-form ab-form"
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <div className="ab-input-group">
                        <div className="ab-input-field-cnt">
                            <Input
                                type="text"
                                value={shareLink}
                                id="share-link"
                                label={Lang?.shareLinkText}
                                isReadOnly={true}
                            />
                        </div>
                    </div>

                    <div className="ab-input-group">
                        <div className="ab-input-field-cnt">
                            <Input
                                type="email"
                                placeholder={Lang?.fromEmailText}
                                value={fromEmail}
                                onChange={(event) => {
                                    setFromEmail(event.target.value);
                                }}
                                isInvalid={Boolean(errors?.fromEmail)}
                                errorMsg={errors.fromEmail}
                                isRequired
                                id="from-email"
                                label={Lang?.fromEmailPlaceholder}
                            />
                        </div>
                    </div>

                    <div className="ab-input-group">
                        <div className="ab-input-field-cnt">
                            <Input
                                type="email"
                                placeholder={Lang?.toEmailPlaceholder}
                                value={toEmail}
                                onChange={(event) => {
                                    setToEmail(event.target.value);
                                }}
                                isInvalid={Boolean(errors?.toEmail)}
                                errorMsg={errors.toEmail}
                                isRequired
                                id="to-email"
                                label={Lang?.toEmailText}
                            />
                        </div>
                    </div>

                    <div className="ab-input-group">
                        <div className="ab-input-field-cnt">
                            <Input
                                type="textarea"
                                isRequired
                                value={message}
                                onChange={(event) => {
                                    setMessage(event.target.value);
                                }}
                                isInvalid={Boolean(errors?.message)}
                                errorMsg={errors.message}
                                id="share-hotel-message"
                                // label={Lang?.message}
                                placeholder={Lang?.shareMessagePlaceholder}
                            />
                        </div>
                    </div>
                    {!!reCapatchPublicKey && (
                        <div className="ab-recaptcha-cnt">
                            <ReCAPTCHA
                                sitekey={reCapatchPublicKey}
                                onChange={(value) => setReCaptchaValue(value)}
                                ref={reCaptchaRef}
                            />
                            {errors.reCaptcha && (
                                <div className="ab-input-error-msg ab-input-error-msg--recaptcha">
                                    {errors.reCaptcha}
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        type="submit"
                        style={{
                            backgroundColor: colors?.primaryBackgroundColor,
                            color: colors?.primaryTextColor,
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? <BarsLoadingIndicator /> : Lang?.share}
                    </button>
                </form>
            </div>
        );
    };

    if (!shareLink) {
        return null;
    }

    return (
        <Modal
            className="ab-modal--share-hotel"
            color={colors?.primaryTextColor}
            backgroundColor={colors?.primaryBackgroundColor}
            isOpen={isOpen}
            onClose={handleModalClose}
            onOverlayClick={handleModalClose}
            placement="center"
            renderContent={renderContent}
            styles={{
                closeIcon: { color: colors?.primaryTextColor },
            }}
            title={Lang?.share}
            variant="secondary"
            width={400}
        />
    );
};

export default ShareHotelModal;
